import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Layers,
  ShieldCheck,
  Microscope,
  Hammer,
  BookOpenText,
  Cpu,
  Gem,
  Eye,
} from "lucide-react";
import StoneLogo from "@/components/StoneLogo";
import { Button } from "@/components/ui/button";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/67551053-ad4a-421d-bffc-ea4a73a95dee/images/6fbaf924ee69447f4507453d19caf9fa3271908042b8e7fb359f8c95ac3f3899.png";
const TECH_BG =
  "https://static.prod-images.emergentagent.com/jobs/67551053-ad4a-421d-bffc-ea4a73a95dee/images/38ae0edc05845ee5a431079fd1f357a3d9a4e6988f7bf4f76f9ef03a9b4f8ca3.png";
const MINERAL_SHOWCASE =
  "https://static.prod-images.emergentagent.com/jobs/67551053-ad4a-421d-bffc-ea4a73a95dee/images/50614b6e48d9e47d4fbe65611ecf889ce586ea0f05789f51a6cec21d8516ca0d.png";
const ARCH_BG =
  "https://images.unsplash.com/photo-1776935359448-fb11f63c375d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwZGFyayUyMG1hcmJsZSUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg1Mjg3NHww&ixlib=rb-4.1.0&q=85";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-[#1f1f1f]"
          : "bg-transparent"
      }`}
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link"><StoneLogo /></Link>
        <nav className="hidden md:flex items-center gap-9 text-[13px] text-zinc-400">
          <a href="#tecnologia" className="hover:text-white transition" data-testid="nav-tech">Tecnologia</a>
          <a href="#aplicacoes" className="hover:text-white transition" data-testid="nav-apps">Aplicações</a>
          <Link to="/library" className="hover:text-white transition" data-testid="nav-library">Biblioteca</Link>
          <Link to="/pro" className="hover:text-white transition" data-testid="nav-pro">StoneIA Pro</Link>
        </nav>
        <Link to="/chat" data-testid="nav-cta-launch">
          <Button className="rounded-full bg-[#F5F5F7] text-black hover:bg-white px-5 h-9 text-[13px] font-medium">
            Iniciar Conversa
          </Button>
        </Link>
      </div>
    </header>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden min-h-screen flex items-center" data-testid="landing-hero">
    <div className="absolute inset-0">
      <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/70 to-[#0A0A0A]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 w-full">
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-[11px] tracking-[0.32em] uppercase text-zinc-400 mb-8"
      >
        Inteligência Mineral · Edição Premium
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.1 }}
        className="silver-text text-5xl md:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tighter font-light max-w-5xl"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        A maior inteligência artificial especializada em pedras decorativas do planeta.
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.25 }}
        className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl font-light leading-relaxed"
      >
        Conhecimento técnico, aplicações, manutenção e engenharia mineral em uma única inteligência.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4 }}
        className="mt-12 flex flex-col sm:flex-row gap-3"
      >
        <Link to="/chat" data-testid="hero-cta-start">
          <Button className="rounded-full bg-[#F5F5F7] text-black hover:bg-white px-7 h-12 text-[14px] font-medium group">
            Conversar com a StoneIA
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </Link>
        <Link to="/library" data-testid="hero-cta-library">
          <Button variant="ghost" className="rounded-full text-zinc-200 hover:text-white hover:bg-white/5 px-7 h-12 text-[14px] font-medium border border-[#262626]">
            Explorar a Biblioteca
          </Button>
        </Link>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.7 }}
        className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1c1c1e] rounded-2xl overflow-hidden border border-[#1f1f1f] max-w-4xl"
        data-testid="hero-stats"
      >
        {[
          { v: "1.200+", l: "Pedras catalogadas" },
          { v: "47", l: "Patologias minerais" },
          { v: "PT · EN · IT", l: "Idiomas técnicos" },
          { v: "24/7", l: "Disponibilidade" },
        ].map((s, i) => (
          <div key={i} className="bg-[#0F0F10] p-6">
            <div className="text-2xl md:text-3xl font-light text-white tracking-tight" style={{ fontFamily: "Outfit" }}>{s.v}</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-2">{s.l}</div>
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

const ChatPreview = () => (
  <section className="relative py-32 px-6 lg:px-10 max-w-7xl mx-auto" data-testid="landing-preview">
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <p className="text-[11px] tracking-[0.32em] uppercase text-zinc-500 mb-5">A interface</p>
        <h2 className="text-4xl md:text-5xl font-light tracking-tighter mb-6" style={{ fontFamily: "Outfit" }}>
          Conversa fluida. <br/><span className="text-zinc-400">Conhecimento absoluto.</span>
        </h2>
        <p className="text-zinc-400 leading-relaxed text-[15px] max-w-md">
          Streaming em tempo real. Análise visual de pedras por imagem. Leitura de catálogos PDF.
          Memória técnica entre conversas. Tudo sob uma única entidade — a StoneIA.
        </p>
      </div>
      <div className="lg:col-span-7">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="rounded-2xl border border-[#262626] bg-[#101010] overflow-hidden shadow-[0_30px_120px_-20px_rgba(0,0,0,0.8)]"
        >
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#1f1f1f]">
            <div className="w-2.5 h-2.5 rounded-full bg-[#2a2a2a]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#2a2a2a]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#2a2a2a]" />
            <div className="ml-auto text-[11px] text-zinc-500 tracking-wider">StoneIA · Sessão</div>
          </div>
          <div className="p-6 space-y-6 min-h-[360px]">
            <div className="flex justify-end">
              <div className="bg-[#1a1a1a] border border-[#262626] rounded-3xl px-5 py-3 text-[14px] max-w-md text-zinc-100 font-light">
                Posso usar Calacatta Borghini em bancada de cozinha em LA?
              </div>
            </div>
            <div className="text-zinc-200 text-[14px] leading-relaxed max-w-2xl font-light">
              <p>O Calacatta Borghini é um <strong>mármore italiano</strong> de altíssima referência estética, porém com comportamento mineral delicado:</p>
              <ul className="list-disc pl-5 mt-2 space-y-1 text-zinc-300">
                <li>Mohs 3.5 — sensível a ácidos cítricos e produtos de limpeza alcalinos.</li>
                <li>Recomenda-se impermeabilização nano-cristalina antes da entrega.</li>
                <li>Para bancada de cozinha intensa, prefira o acabamento <em>levigato</em>.</li>
              </ul>
              <p className="mt-3 text-zinc-400">Quer comparar com o Quartzito Patagonia para o mesmo ambiente?</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const Feature = ({ icon: Icon, title, desc, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: idx * 0.08 }}
    className="p-7 rounded-2xl border border-[#1f1f1f] bg-[#0F0F10] hover:border-[#333] transition-all duration-300"
  >
    <Icon className="h-5 w-5 text-zinc-300 mb-5" strokeWidth={1.4} />
    <h3 className="text-[17px] mb-2 font-medium text-white" style={{ fontFamily: "Outfit" }}>{title}</h3>
    <p className="text-[13.5px] text-zinc-400 leading-relaxed">{desc}</p>
  </motion.div>
);

const Features = () => (
  <section id="tecnologia" className="relative py-32 px-6 lg:px-10 max-w-7xl mx-auto" data-testid="landing-features">
    <div className="max-w-3xl mb-16">
      <p className="text-[11px] tracking-[0.32em] uppercase text-zinc-500 mb-5">Tecnologia</p>
      <h2 className="text-4xl md:text-5xl font-light tracking-tighter" style={{ fontFamily: "Outfit" }}>
        Arquitetura multi-IA <br/><span className="text-zinc-400">orquestrada como uma só.</span>
      </h2>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
      <Feature idx={0} icon={Sparkles} title="Streaming em tempo real" desc="Respostas técnicas surgem palavra a palavra — fluidez de um pensamento mineral." />
      <Feature idx={1} icon={Eye} title="Reconhecimento visual" desc="Envie a foto da pedra. A StoneIA identifica, classifica e diagnostica patologias." />
      <Feature idx={2} icon={BookOpenText} title="Leitura de catálogos PDF" desc="Carregue ficha técnica em PDF e converse sobre o material em segundos." />
      <Feature idx={3} icon={Layers} title="Memória de conversa" desc="Contexto técnico preservado — comparações, escolhas e detalhes de obra mantidos." />
      <Feature idx={4} icon={ShieldCheck} title="Especialização absoluta" desc="Responde somente sobre o universo mineral. Sem distrações, sem ruído." />
      <Feature idx={5} icon={Cpu} title="Multi-modelo invisível" desc="Internamente orquestra os melhores modelos. Externamente, uma única inteligência." />
    </div>
  </section>
);

const Applications = () => (
  <section id="aplicacoes" className="relative py-32 px-6 lg:px-10" data-testid="landing-applications">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#1f1f1f]">
        <img src={ARCH_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-transparent to-black/20" />
      </div>
      <div>
        <p className="text-[11px] tracking-[0.32em] uppercase text-zinc-500 mb-5">Aplicações</p>
        <h2 className="text-4xl md:text-5xl font-light tracking-tighter mb-8" style={{ fontFamily: "Outfit" }}>
          Do projeto à obra. <br/><span className="text-zinc-400">Da pedra à arquitetura.</span>
        </h2>
        <div className="space-y-5">
          {[
            { icon: Microscope, t: "Diagnóstico mineral", d: "Patologias, manchas, fissuras, eflorescência, oxidação." },
            { icon: Hammer, t: "Engenharia de instalação", d: "Paginação, corte, usinagem, comportamento estrutural sob carga." },
            { icon: Gem, t: "Curadoria de materiais", d: "Mármores, quartzitos, ônix, travertinos, sinterizados e porcelânicos minerais." },
          ].map((it, i) => (
            <div key={i} className="flex gap-4 items-start py-5 border-t border-[#1f1f1f]" data-testid={`landing-app-row-${i}`}>
              <it.icon className="h-5 w-5 text-zinc-300 mt-0.5" strokeWidth={1.4} />
              <div>
                <div className="text-[15px] text-white font-medium mb-1" style={{ fontFamily: "Outfit" }}>{it.t}</div>
                <div className="text-[13.5px] text-zinc-400">{it.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ProBand = () => (
  <section className="relative py-32 px-6 lg:px-10" data-testid="landing-pro-band">
    <div className="relative max-w-7xl mx-auto rounded-3xl overflow-hidden border border-[#1f1f1f]">
      <img src={TECH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
      <div className="relative px-8 md:px-14 py-20 max-w-2xl">
        <p className="text-[11px] tracking-[0.32em] uppercase text-zinc-500 mb-5">StoneIA Pro</p>
        <h2 className="text-4xl md:text-5xl font-light tracking-tighter mb-6" style={{ fontFamily: "Outfit" }}>
          Inteligência mineral <br/>sem limites.
        </h2>
        <p className="text-zinc-400 mb-8 max-w-lg leading-relaxed">
          Conversas ilimitadas, análise visual avançada, leitura de catálogos PDF, comparação de materiais
          e acesso prioritário à biblioteca mineral mundial.
        </p>
        <Link to="/pro" data-testid="landing-cta-pro">
          <Button className="rounded-full bg-[#F5F5F7] text-black hover:bg-white px-7 h-12 text-[14px] font-medium">
            Conhecer o StoneIA Pro
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-[#1f1f1f] mt-10">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
      <StoneLogo />
      <div className="flex flex-col gap-1.5 text-[12px] text-zinc-500 tracking-wide">
        <span>© {new Date().getFullYear()} StoneIA · Inteligência Mineral Premium</span>
        <span className="text-[11px] text-zinc-600" data-testid="footer-credit">
          Created by <span className="text-zinc-500">Camasa Mármores &amp; Design</span>
        </span>
      </div>
      <div className="flex gap-6 text-[12px] text-zinc-500">
        <Link to="/library" className="hover:text-white transition">Biblioteca</Link>
        <Link to="/pro" className="hover:text-white transition">Pro</Link>
        <Link to="/chat" className="hover:text-white transition">Chat</Link>
      </div>
    </div>
  </footer>
);

const LandingPage = () => (
  <div className="bg-[#0A0A0A] text-white min-h-screen" data-testid="landing-page">
    <Nav />
    <Hero />
    <ChatPreview />
    <Features />
    <Applications />
    <ProBand />
    <Footer />
  </div>
);

export default LandingPage;
